import React, { useMemo } from "react";
import Color from "colorjs.io";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { Typography } from "antd";
import CircularGauge from "@/app/components/charts/CircularGauge";
import useAxiosQuery from "@/hooks/useMyQuery";
import BaseSlide from "./BaseSlide";

// Define 7 colors for the Fear and Greed gradient
const colors = [
  "#FF4D4F", // Extreme Fear (Red)
  "#FF7A45", // Fear (Orange)
  "#FFA940", // Moderate Fear (Light Orange)
  "#FADB14", // Neutral (Yellow)
  "#A0D911", // Moderate Greed (Light Green)
  "#73D13D", // Greed (Green)
  "#52C41A", // Extreme Greed (Dark Green)
];

const getColorByScore = (score: number): string => {
  // Map score to color index
  const index = Math.floor((score / 100) * (colors.length - 1));
  const nextIndex = Math.min(index + 1, colors.length - 1);
  const progress = (score / 100) * (colors.length - 1) - index;

  // Interpolate between two closest colors
  const currentColor = new Color(colors[index]);
  const nextColor = new Color(colors[nextIndex]);
  const range = currentColor.range(nextColor);

  return range(progress).toString({ format: "hex" });
};

// Types
type IGauge = {
  value: number;
  type: string;
};

interface IStatisticGauge {
  now: IGauge;
  yesterday: IGauge;
  last_week: IGauge;
  last_month: IGauge;
}

const FearGreedSlide: React.FC = () => {
  const t = useTranslations("home");

  // Fear and Greed Index API
  const { data: fgResponse, isLoading: isFgLoading } = useAxiosQuery<{ success: boolean; data: IStatisticGauge }>({
    url: "/v1/feed/public/market-gauge",
    method: "get",
  });

  const fearGreedData = useMemo(() => fgResponse?.data, [fgResponse?.data]);

  const historicalData = useMemo(
    () => [
      {
        label: t("Yesterday"),
        value: fearGreedData?.yesterday?.value ?? 0,
        type: fearGreedData?.yesterday?.type ?? "",
      },
      {
        label: t("7d_ago"),
        value: fearGreedData?.last_week?.value ?? 0,
        type: fearGreedData?.last_week?.type ?? "",
      },
      {
        label: t("1m_ago"),
        value: fearGreedData?.last_week?.value ?? 0,
        type: fearGreedData?.last_month?.type ?? "",
      },
    ],
    [fearGreedData, t],
  );

  // Custom content for the Fear and Greed slide
  const CustomContent = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="flex items-center justify-center gap-6"
    >
      <CircularGauge value={fearGreedData?.now?.value ?? 0} size="default" label={fearGreedData?.now?.type} />

      <div className="flex flex-wrap justify-center gap-4">
        {historicalData.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="flex flex-col items-center"
          >
            <CircularGauge value={item.value} size="small" className="mb-1" />
            <div className="text-center text-[10px]">
              <Typography.Text className="text-[10px]">{item.label}: </Typography.Text>
              <Typography.Text className="text-[10px]" style={{ color: getColorByScore(item.value) }}>
                {item.type}
              </Typography.Text>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <BaseSlide
      title={t("fear_and_greed_index")}
      isLoading={isFgLoading}
      data={{ value: fearGreedData?.now?.value?.toString() ?? "0" }}
    >
      <CustomContent />
    </BaseSlide>
  );
};

export default FearGreedSlide;
